import { useStore } from "@tanstack/react-form";
import { createContext, ReactNode, useContext, useMemo } from "react";

import { PublicMaintenance } from "api/public/project/maintenance/maintenance.types";
import { useGetAllPclProductsQuery } from "api/public/project/product/product.api";
import {
  PublicPclProduct,
  PublicPclPp,
} from "api/public/project/product/product.types";
import { AppFormType, useFormContext } from "components/Forms/AppForm";

import { filterProducts } from "../components/Filter/FilterUtils";
import { PclFilterOptions } from "../PublicProducts.screen";

export interface PclProduct extends PublicPclProduct {
  protection_profiles: PublicPclPp[];
  active_maintenances: PublicMaintenance[];
  tech_types: string[];
}

const usePclProducts = () => {
  const form = useFormContext() as unknown as AppFormType;
  // this is ugly but might be necessary at the moment? - 4/27/25
  // https://github.com/TanStack/form/discussions/1335

  const { data: allProducts, isLoading: productsLoading } =
    useGetAllPclProductsQuery();

  const filters = useStore(
    form.store,
    (state) => state.values as PclFilterOptions,
  );

  const isLoading = productsLoading;

  const filteredProducts = useMemo(() => {
    if (!allProducts) {
      return [];
    }

    if (!filters) {
      return allProducts;
    }

    const filteredProducts = filterProducts(allProducts, filters);

    return filteredProducts;
  }, [filters, allProducts]);

  return { products: filteredProducts, isLoading, filters, form };
};

interface PclContextType {
  products: PclProduct[];
  isLoading: boolean;
  filters: PclFilterOptions;
  form: AppFormType;
}

const PclContext = createContext<PclContextType | null>(null);

// This is the Provider
// Any thing that is wrapped in this will be able to call usePclContext to access
// the filtered products, the combined loading status, the current filters, and the formApi
export const PclContextProvider = ({ children }: { children: ReactNode }) => {
  const { products, isLoading, filters, form } = usePclProducts();

  return (
    <PclContext.Provider value={{ products, isLoading, filters, form }}>
      {children}
    </PclContext.Provider>
  );
};

// Anything that is wrapped in the PclContextProvider can call this to use the Pcl data
// It will throw a warning to remind you if you use it in a component not wrapped in the provider
export const usePclContext = () => {
  const context = useContext(PclContext);

  if (!context)
    throw new Error("usePclContext must be used within a PclContextProvider");

  return context;
};
